// One profile picture for all circles on this device (38-desyr-id-picture-key.md, Behaviour 1, v1.58.50):
// each circle's record `chat.desyr.picture` holds the picture's hash, its revision and when it was set. A
// change is set in every other circle; at each start the winning record is set where it differs. Plain
// calls with each circle's own session (no client, no sync), one circle after the other in the switcher's
// order, nothing shown; a circle that fails is left as it is until the next start. Never a call to the
// desyr ID service. Each record also keeps the mxc address desyr set in that circle (v1.58.54): a circle
// whose own profile picture differs from it was changed outside desyr and gets the winner's picture again.
// v1.58.63: the highest revision wins, not the clock (1.11), and only a record this device wrote or two
// circles hold (1.9); a copied file is checked against the limits (1.10).
import type { MatrixClient } from 'matrix-js-sdk';
import { everyAccount } from '../accounts/store';
import { ask, type Who } from '../core/session';
import { readJson, writeJson } from '../core/storage';
import { readLimited, withinLimits } from './pictureFile';
import { readId, withId } from './store';

export interface PictureRecord {
  /** SHA-256 of the file's bytes, lowercase hex; null: no picture. */
  hash: string | null;
  /** The revision (v1.58.63); absent in records from before it, which count as 0. */
  rev?: number;
  /** Milliseconds since 1970, this device's clock: only the tie-break. */
  set_at: number;
  /** The mxc address desyr set in this circle; null: none (v1.58.54). Absent in records from before it. */
  mxc?: string | null;
}
type Change = Pick<PictureRecord, 'hash' | 'rev' | 'set_at'>;

const RECORD = 'chat.desyr.picture';
const ok = (r: Response) => (r.ok ? Promise.resolve(r) : Promise.reject(r.status));
const json = (body: unknown): RequestInit => ({ method: 'PUT', body: JSON.stringify(body) });

/** The file's hash for the record (38, Data). */
export async function hashOf(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const revOf = (r: Change) => r.rev ?? 0;
/** The order of 1.11: the revision, then the later `set_at`, then the larger hash (none below any). */
export function newer(a: Change, b: Change): number {
  if (revOf(a) !== revOf(b)) return revOf(a) - revOf(b);
  if (a.set_at !== b.set_at) return a.set_at - b.set_at;
  if (a.hash === b.hash) return 0;
  if (a.hash === null || b.hash === null) return a.hash === null ? -1 : 1;
  return a.hash < b.hash ? -1 : 1; // lowercase hex compared as text
}
const same = (a: Change, b: Change) => a.hash === b.hash && revOf(a) === revOf(b) && a.set_at === b.set_at;
const change = (r: Change): Change => ({ hash: r.hash, rev: revOf(r), set_at: r.set_at });

/** `desyr_picture_last`: per desyr ID, the record this device last wrote or copied (1.9, v1.58.63). */
const LAST = 'desyr_picture_last';
export function lastHere(): Change | undefined {
  const sub = readId()?.sub;
  return sub ? readJson<Record<string, Change>>(LAST)?.[sub] : undefined;
}
function keepLast(r: Change): void {
  const sub = readId()?.sub;
  if (sub) writeJson(LAST, { ...readJson<Record<string, Change>>(LAST), [sub]: change(r) });
}

/** A record may win (1.9): this device wrote or copied it, or at least two circles hold it. */
export const trusted = (r: Change, records: Change[], last = lastHere()) => (!!last && same(r, last)) || records.filter((x) => same(x, r)).length >= 2;

/** My account's line under the picture (Behaviour 1.6): with desyr ID and more than one circle here. */
export const oneForAll = (client: MatrixClient) => withId() && everyAccount(client).length > 1;

const recordPath = (a: Who) => `/v3/user/${encodeURIComponent(a.user_id)}/account_data/${RECORD}`;
/** The circle's record; null when it has none yet. */
async function readRecord(a: Who): Promise<PictureRecord | null> {
  const answer = await ask(a, recordPath(a));
  if (answer.status === 404) return null;
  const r = await ok(answer).then((x) => x.json());
  if (typeof r?.set_at !== 'number' || (r.hash !== null && typeof r.hash !== 'string')) return null;
  return { hash: r.hash, set_at: r.set_at, ...(Number.isInteger(r.rev) ? { rev: r.rev } : {}), ...(r.mxc === null || typeof r.mxc === 'string' ? { mxc: r.mxc } : {}) };
}
const writeRecord = (a: Who, record: PictureRecord) => ask(a, recordPath(a), json(record)).then(ok);

/** The circle's server says the picture may not be changed (21, rule 3): it keeps its own (Behaviour 1.4). */
const refuses = (a: Who) =>
  ask(a, '/v3/capabilities')
    .then(ok)
    .then((r) => r.json())
    .then((c) => c?.capabilities?.['m.set_avatar_url']?.enabled === false);

const avatarPath = (a: Who) => `/v3/profile/${encodeURIComponent(a.user_id)}/avatar_url`;
/** The circle's current picture (an mxc URI), or none. */
async function avatarOf(a: Who): Promise<string | undefined> {
  const answer = await ask(a, avatarPath(a));
  if (answer.status === 404) return undefined;
  return (await ok(answer).then((r) => r.json()))?.avatar_url || undefined;
}

/** The file of an mxc URI, from the circle's own server with its session, read only up to 5 MiB (1.10). */
async function download(a: Who, mxc: string): Promise<Uint8Array<ArrayBuffer> | undefined> {
  const [, server, id] = mxc.match(/^mxc:\/\/([^/]+)\/(.+)$/) ?? [];
  if (!server) throw new Error('not a picture');
  return ask(a, `/v1/media/download/${server}/${id}`).then(ok).then((r) => readLimited(r));
}

/**
 * Sets the picture in one circle: the same file uploaded to its own server (never a link to another's),
 * or none; then its record, with the mxc address set there.
 */
async function setIn(a: Who, record: Change, file?: Blob): Promise<void> {
  let mxc = '';
  if (file) {
    const up = await fetch(`${a.base_url}/_matrix/media/v3/upload`, { method: 'POST', body: file, headers: { Authorization: `Bearer ${a.access_token}`, 'Content-Type': file.type || 'application/octet-stream' } });
    mxc = (await ok(up).then((r) => r.json())).content_uri;
  }
  await ask(a, avatarPath(a), json({ avatar_url: mxc })).then(ok);
  await writeRecord(a, { ...change(record), mxc: mxc || null });
}

/** The open circle's picture changed: the profile is read again (Account's `meChanged`). */
const meChanged = () => dispatchEvent(new Event('desyr:me'));

/** One pass at a time; a change made while the start's pass runs stops it (the change is the latest). */
let running = Promise.resolve();
let changes = 0;
const queue = (work: () => Promise<void>) => (running = running.then(work).catch(() => {}));
/** The highest revision read at this start, or written since (1.11). */
let seen = 0;
/** The picture this tab holds already (set or fetched): never fetched again (1.5, v1.58.63). */
let held: { hash: string; file: Blob } | undefined;

/**
 * One's own picture changed in the open circle (Behaviour 1.1): its record (with `mxc`, the address set
 * there) with the next revision, then the same picture in every other circle here that may change it.
 * `file` null: removed (a change like any other).
 */
export function pictureChanged(client: MatrixClient, file: Blob | null, mxc: string | null = null): Promise<void> {
  if (!withId()) return running;
  changes++;
  const [open, ...others] = everyAccount(client);
  const at = Date.now(); // the change's time, even when it waits for a pass under way
  return queue(async () => {
    const before = await readRecord(open).catch(() => null);
    const rev = Math.max(seen, revOf(lastHere() ?? { hash: null, set_at: 0 }), before ? revOf(before) : 0) + 1;
    const hash = file && (await hashOf(file));
    const record: Change = { hash, rev, set_at: at };
    if (file && hash) held = { hash, file };
    seen = rev;
    await writeRecord(open, { ...record, mxc: file ? mxc : null }).then(() => keepLast(record), () => {});
    for (const a of others) await (async () => (await refuses(a)) || (await setIn(a, record, file ?? undefined)))().catch(() => {});
  });
}

interface Circle {
  a: Who;
  record: PictureRecord | null;
  refuses: boolean;
  mxc?: string;
}

/**
 * At each start, once the open circle's first sync is through (Behaviour 1.2–1.5, 1.9–1.11): every
 * circle's record read with its own session; the highest revision among the records this device may
 * trust wins and is set in every circle that differs, never over a newer record held by one circle
 * alone. The first time (no record anywhere) the open circle's picture, else the first one's in the
 * switcher's order, becomes the record.
 */
export function startOnePicture(client: MatrixClient): Promise<void> {
  if (!withId()) return running;
  const all = everyAccount(client);
  return queue(async () => {
    const began = changes;
    const read = await Promise.all(all.map((a) => (async (): Promise<Circle> => ({ a, record: await readRecord(a), refuses: await refuses(a), mxc: await avatarOf(a) }))().catch(() => undefined)));
    const known = read.filter((c): c is Circle => !!c);
    const may = known.filter((c) => !c.refuses);
    const records = known.flatMap((c) => (c.record ? [c.record] : []));
    seen = Math.max(0, ...records.map(revOf)); // the highest read at this start (1.11)
    // A refusing circle never wins; nor does a record only one circle holds that this device didn't write.
    const candidates = may.flatMap((c) => (c.record && trusted(c.record, records) ? [c.record] : []));
    let win: Change | undefined = candidates.reduce<Change | undefined>((w, r) => (!w || newer(r, w) > 0 ? r : w), undefined);
    let file: Blob | undefined;
    if (!win) {
      // The first time: only once every circle could say it has no record (else the next start).
      if (records.length) return;
      const source = known.length === all.length && may.find((c) => c.mxc);
      if (!source) return;
      file = await download(source.a, source.mxc!).then((bytes) => bytes && withinLimits(bytes));
      if (!file) return;
      win = { hash: await hashOf(file), rev: Math.max(seen, revOf(lastHere() ?? { hash: null, set_at: 0 })) + 1, set_at: Date.now() };
      source.record = { ...win, mxc: source.mxc! };
      await writeRecord(source.a, source.record);
      keepLast(win);
    }
    const winner = win;
    if (winner.hash && held?.hash === winner.hash) file ??= held.file;
    for (const c of may) {
      if (changes !== began) return;
      if (c.record && newer(c.record, winner) > 0) continue; // a newer record this device can't trust: left as it is
      if (agrees(c, winner)) {
        // The same picture: nothing uploaded. A record from before v1.58.54 learns the address desyr finds
        // there, and one with another revision or time takes the winner's (a copy keeps them, 1.11).
        if (c.record && (c.record.mxc === undefined || !same(c.record, winner))) await writeRecord(c.a, { ...change(winner), mxc: c.record.mxc === undefined ? (c.mxc ?? null) : c.record.mxc }).catch(() => {});
        continue;
      }
      // The file fetched once, from a circle holding the winning picture (the winner first), its bytes checked.
      if (winner.hash && !file) file = await fetchWinner(may, winner);
      if (winner.hash && !file) return;
      const done = await setIn(c.a, winner, file).then(() => true, () => false);
      if (done && c.a === all[0]) meChanged();
    }
    keepLast(winner);
  });
}

/**
 * A circle agrees when its record has the winner's hash and its own profile picture is the one desyr set
 * there (1.5, v1.58.54): the record's `mxc`, compared by address only, never by downloading. A record
 * from before v1.58.54 (no `mxc`) agrees when the circle has a picture exactly when the winner does.
 */
export function agrees(c: { record: PictureRecord | null; mxc?: string }, win: Change): boolean {
  if (c.record?.hash !== win.hash) return false;
  if (c.record.mxc === undefined) return !!c.mxc === !!win.hash;
  return (c.mxc ?? null) === c.record.mxc && !!c.mxc === !!win.hash;
}

/** From a circle still holding what desyr set there: at most 5 MiB, the hash checked, decoded within 4096 px (1.8, 1.10). */
async function fetchWinner(may: Circle[], win: Change): Promise<Blob | undefined> {
  // Only from a circle still holding what desyr set there (one changed outside desyr holds another picture).
  const exact = (c: Circle) => (c.record && same(c.record, win) ? 1 : 0);
  const holders = may.filter((c) => c.mxc && agrees(c, win)).sort((x, y) => exact(y) - exact(x));
  for (const c of holders) {
    const bytes = await download(c.a, c.mxc!).catch(() => undefined);
    if (!bytes || (await hashOf(new Blob([bytes]))) !== win.hash) continue;
    const file = await withinLimits(bytes);
    if (!file) return undefined; // the winning picture itself is outside the limits: nothing is copied
    held = { hash: win.hash!, file };
    return file;
  }
  return undefined;
}
